import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { studentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import type { Student, StudentStatus } from '@/types/domain';

type LifecycleAction = 'suspend' | 'reinstate' | 'withdraw' | 'graduate' | 'readmit';

interface ActionCopy {
  label: string;
  title: string;
  explanation: string;
  confirmLabel: string;
  done: string;
  /** Mirrors the API: these endpoints refuse a blank reason. */
  reasonRequired: boolean;
  /** Graduation takes no reason at all, so it is a plain confirmation. */
  takesReason: boolean;
  destructive: boolean;
}

const ACTIONS: Record<LifecycleAction, ActionCopy> = {
  suspend: {
    label: 'Suspend',
    title: 'Suspend student',
    explanation: 'The student stays enrolled but is marked suspended until reinstated.',
    confirmLabel: 'Suspend',
    done: 'Student suspended',
    reasonRequired: true,
    takesReason: true,
    destructive: true,
  },
  reinstate: {
    label: 'Reinstate',
    title: 'Reinstate student',
    explanation: 'Returns the student to active status.',
    confirmLabel: 'Reinstate',
    done: 'Student reinstated',
    reasonRequired: false,
    takesReason: true,
    destructive: false,
  },
  withdraw: {
    label: 'Withdraw',
    title: 'Withdraw student',
    explanation:
      'The student leaves the school. Their live enrolment is closed as withdrawn; the record and its history are kept.',
    confirmLabel: 'Withdraw',
    done: 'Student withdrawn',
    reasonRequired: true,
    takesReason: true,
    destructive: true,
  },
  graduate: {
    label: 'Graduate',
    title: 'Graduate student',
    explanation: 'Marks the student as graduated. Readmission is possible later if needed.',
    confirmLabel: 'Graduate',
    done: 'Student graduated',
    reasonRequired: false,
    takesReason: false,
    destructive: false,
  },
  readmit: {
    label: 'Readmit',
    title: 'Readmit student',
    explanation:
      'Returns the student to active status. They will need a fresh enrolment to be placed in a grade.',
    confirmLabel: 'Readmit',
    done: 'Student readmitted',
    reasonRequired: true,
    takesReason: true,
    destructive: false,
  },
};

/*
 * Which moves to offer from each status. This only decides which buttons are
 * worth showing — the server validates every transition and its refusal is
 * shown verbatim, so a stale page cannot push a student somewhere invalid.
 */
const MOVES: Record<StudentStatus, LifecycleAction[]> = {
  ACTIVE: ['suspend', 'withdraw', 'graduate'],
  SUSPENDED: ['reinstate', 'withdraw'],
  INACTIVE: ['reinstate', 'withdraw'],
  WITHDRAWN: ['readmit'],
  TRANSFERRED: ['readmit'],
  GRADUATED: ['readmit'],
};

/**
 * The student's status changes, each a named transition.
 *
 * These replace the status dropdown the profile form used to carry. A dropdown
 * let anyone jump a student from suspended to graduated with no reason and no
 * trace; each action here is validated by the server and writes a row to the
 * status history.
 */
export function StudentLifecycleMenu({ student }: { student: Student }) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [menuOpen, setMenuOpen] = useState(false);
  const [pending, setPending] = useState<LifecycleAction | null>(null);
  const [reason, setReason] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Dismiss on an outside click or Escape, as any menu should.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current !== null && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const run = useMutation({
    mutationFn: (action: LifecycleAction) => {
      const trimmed = reason.trim();
      switch (action) {
        case 'suspend':
          return studentsApi.suspend(student.id, trimmed);
        case 'reinstate':
          return studentsApi.reinstate(student.id, trimmed === '' ? undefined : trimmed);
        case 'withdraw':
          return studentsApi.withdraw(student.id, trimmed);
        case 'graduate':
          return studentsApi.graduate(student.id);
        case 'readmit':
          return studentsApi.readmit(student.id, trimmed);
      }
    },
    onSuccess: (_, action) => {
      toast.success(ACTIONS[action].done);
      close();
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.studentProfile.all });
    },
    onError: (error) => handleApiError(error),
  });

  const close = () => {
    setPending(null);
    setReason('');
  };

  const moves = MOVES[student.status];
  if (moves.length === 0) {
    return null;
  }

  const copy = pending === null ? null : ACTIONS[pending];

  return (
    <>
      <div ref={menuRef} className="relative">
        <Button
          variant="secondary"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          Status
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </Button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-line bg-surface py-1 shadow-lg"
          >
            {moves.map((action) => (
              <button
                key={action}
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
                onClick={() => {
                  setMenuOpen(false);
                  setPending(action);
                }}
              >
                {ACTIONS[action].label}
              </button>
            ))}
          </div>
        )}
      </div>

      {copy !== null && pending !== null && !copy.takesReason && (
        <ConfirmDialog
          open
          title={copy.title}
          message={`${student.fullName}: ${copy.explanation}`}
          confirmLabel={copy.confirmLabel}
          destructive={copy.destructive}
          loading={run.isPending}
          onConfirm={() => run.mutate(pending)}
          onCancel={close}
        />
      )}

      {copy !== null && pending !== null && copy.takesReason && (
        <Modal
          open
          title={copy.title}
          description={student.fullName}
          onClose={close}
          footer={
            <>
              <Button variant="secondary" onClick={close} disabled={run.isPending}>
                Cancel
              </Button>
              <Button
                variant={copy.destructive ? 'danger' : 'primary'}
                loading={run.isPending}
                disabled={copy.reasonRequired && reason.trim() === ''}
                onClick={() => run.mutate(pending)}
              >
                {copy.confirmLabel}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-muted">{copy.explanation}</p>
            <TextField
              label="Reason"
              required={copy.reasonRequired}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              hint={copy.reasonRequired ? 'Recorded in the status history' : 'Optional; recorded in the status history'}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
